import { take, call, put, select, takeLatest } from "redux-saga/effects";
import { LanguageSchema } from "../../i18n";
import { httpGet, httpDelete, httpPost, httpPut } from "../../services";
import cloneDeep from "lodash/cloneDeep";

import {
  IntegrationsConst,
  GetDataExportDumpRequestsResponseSchema,
  getDataExportDumpRequestsLoadingAction,
  getDataExportDumpRequestsSuccessAction,
  getDataExportDumpRequestsErrorAction,
  RemoveDataExportDumpRequestResponseSchema,
  removeDataExportDumpRequestLoadingAction,
  removeDataExportDumpRequestSuccessAction,
  removeDataExportDumpRequestErrorAction,
  addDataExportDumpRequestLoadingAction,
  addDataExportDumpRequestSuccessAction,
  addDataExportDumpRequestErrorAction,
  DataExportRequestSchema,
  getExportLogsLoadingAction,
  GetExportLogsSchema,
  getExportLogsSuccessAction,
  getExportLogsErrorAction,
  getExportLogDownloadUrlLoadingAction,
  GetExportLogDownloadUrlSchema,
  getExportLogDownloadUrlSuccessAction,
  getExportLogDownloadUrlErrorAction,
  getWebHookRequestsLoadingAction,
  getWebHookRequestsSuccessAction,
  getWebHookRequestsErrorAction,
  GetWebHookRequestsSchema,
  deleteWebHookLoadingAction,
  deleteWebHookSuccessAction,
  deleteWebHookErrorAction,
  updateWebHookLoadingAction,
  updateWebHookErrorAction,
  AddWebhookRequestSchema,
  DeleteWebhookRequestSchema,
  getWebHookRequestsAction,
  addWebHookLoadingAction,
  addWebHookSuccessAction,
  addWebHookErrorAction,
  AvailableIntegrationsSchema,
  getAvailableIntegrationsLoadingAction,
  getAvailableIntegrationsSuccessAction,
  getAvailableIntegrationsErrorAction,
  ActivateIntegrationArgs,
  activateIntegrationLoadingAction,
  activateIntegrationSuccessAction,
  activateIntegrationErrorAction,
  DeactivateIntegrationArgs,
  deactivateIntegrationLoadingAction,
  deactivateIntegrationSuccessAction,
  deactivateIntegrationErrorAction,
  getIntegrationConfigLoadingAction,
  GetIntegrationConfigSchema,
  getIntegrationConfigSuccessAction,
  getIntegrationConfigErrorAction,
  saveIntegrationDataLoadingAction,
  SaveIntegrationDataSchema,
  SaveIntegrationDataArgs,
  saveIntegrationDataSuccessAction,
  saveIntegrationDataErrorAction,
  UpdateWebhookRequestSchema,
  updateWebHookSuccessAction,
  saveAuditLogConfigLoadingAction,
  saveAuditLogConfigSuccessAction,
  saveAuditLogConfigErrorAction,
  updateAuditLogConfigLoadingAction,
  updateAuditLogConfigSuccessAction,
  updateAuditLogConfigErrorAction,
  AuditLogBodySchema,
  getAuditLogConfigAction,
  getAuditLogConfigLoadingAction,
  getAuditLogConfigSuccessAction,
  getAuditLogConfigErrorAction,
  resetAuditLogConfigLoadingAction,
  resetAuditLogConfigSuccessAction,
  resetAuditLogConfigErrorAction,
  getWebhookEventsErrorAction,
  getWebhookEventsSuccessAction,
  getWebhookEventsLoadingAction,
  GetWebhookEventsSchema,
  getAuditLogLimitsLoadingAction,
  getAuditLogLimitsSuccessAction,
  getAuditLogLimitsErrorAction,
  IntegrationSubscription,
  IntegrationSubscriptionsSchema,
  getIntegrationSubscriptionsAction,
  getIntegrationSubscriptionsLoadingAction,
  getIntegrationSubscriptionsSuccessAction,
  getIntegrationSubscriptionsErrorAction
} from "../actions/integrationsActions";

import { RootSchema } from "../reducers";
import { getIntegrationListFailure, getIntegrationListRequest, getIntegrationListSuccess } from "../features/integration/cloudConnectors";
/**
 * Workers
 */
function* getDataExportDumpRequestsHandler() {
  try {
    yield put(getDataExportDumpRequestsLoadingAction());
    const response: GetDataExportDumpRequestsResponseSchema = yield httpGet("/integrations/dataexport/dump");
    if (response.ErrorCode && response.ErrorCode !== 1079) {
      // Error code 1079 means: 'There are no active data dump requests configured for this site.'
      throw Error(response.Message || response.Description || "Error when fetching data dump requests.");
    }
    response.Data = response.Data === undefined ? [] : response.Data;
    yield put(getDataExportDumpRequestsSuccessAction({ response }));
  } catch (e: any) {
    yield put(getDataExportDumpRequestsErrorAction(e.message));
  }
}

function* removeDataExportDumpRequestHandler(dataExportRequestId: string) {
  try {
    yield put(removeDataExportDumpRequestLoadingAction());
    const response: RemoveDataExportDumpRequestResponseSchema = yield httpDelete("/integrations/dataexport/dump", {}, { id: dataExportRequestId });
    if (response.ErrorCode !== undefined) {
      throw Error(response.Message || response.Description || "Error when attempting to remove data dump request.");
    }
    yield put(removeDataExportDumpRequestSuccessAction({ response }));
  } catch (e: any) {
    yield put(removeDataExportDumpRequestErrorAction(e.message));
  }
}

function* addDataExportDumpRequestHandler(dataExportRequest: DataExportRequestSchema) {
  try {
    yield put(addDataExportDumpRequestLoadingAction());
    const response: GetDataExportDumpRequestsResponseSchema = yield httpPost("/integrations/dataexport/dump", {}, { ...dataExportRequest });
    if (response.Description) {
      throw Error(response.Description);
    }
    if (response.lrmessage) {
      throw Error(response.lrmessage);
    }
    yield put(addDataExportDumpRequestSuccessAction({ response }));
  } catch (e: any) {
    yield put(addDataExportDumpRequestErrorAction(e.message));
  }
}

function* getExportLogsHandler() {
  try {
    yield put(getExportLogsLoadingAction());
    const response: GetExportLogsSchema = yield httpGet("/integrations/dataexport/dump/logs");
    if (response.ErrorCode && response.ErrorCode !== 927) {
      // Error code 927 is for no record found for export logs
      throw Error(response.Message || response.Description || "Error when fetching data dump requests.");
    }
    response.Data = response.Data === undefined ? [] : response.Data.slice().reverse();
    yield put(getExportLogsSuccessAction(response));
  } catch (e: any) {
    yield put(getExportLogsErrorAction(e.message));
  }
}

function* getExportLogDownloadUrlHandler(action: any) {
  try {
    yield put(getExportLogDownloadUrlLoadingAction());
    const { s3Path } = action.payload;
    const response: GetExportLogDownloadUrlSchema = yield httpPost("/integrations/dataexport/dump/logs/download-url", {}, { s3Path });
    if (response.ErrorCode && response.ErrorCode !== 0) {
      throw Error(response.Message || response.Description || "Error when generating download URL.");
    }
    yield put(getExportLogDownloadUrlSuccessAction(response));
  } catch (e: any) {
    yield put(getExportLogDownloadUrlErrorAction(e.message));
  }
}

function* getWebHookRequestsHandler() {
  try {
    yield put(getWebHookRequestsLoadingAction());

    const res: GetWebHookRequestsSchema = yield httpGet("/integrations/webhook");
    res.Data = res.Data || [];
    // res.Data = res.Data.filter(v => !!v.Name);

    yield put(getWebHookRequestsSuccessAction(res));
  } catch (e: any) {
    yield put(getWebHookRequestsErrorAction(e.message));
  }
}

function* deleteWebHookHandler(req: DeleteWebhookRequestSchema) {
  try {
    yield put(deleteWebHookLoadingAction());
    const res = yield httpDelete("/integrations/webhook/" + req, {});
    if (res.ErrorCode && res.Description) {
      throw new Error(res.Description);
    }

    yield put(deleteWebHookSuccessAction(res));
    yield put(getWebHookRequestsAction());
  } catch (e: any) {
    yield put(deleteWebHookErrorAction(e.message));
    yield put(getWebHookRequestsAction());
  }
}
function* updateWebHookHandler(req: UpdateWebhookRequestSchema, i18n: LanguageSchema) {
  try {
    yield put(updateWebHookLoadingAction());
    req.SecretName = req.SecretName || "";
    const res = yield httpPut("/integrations/webhook/" + req.HookId, {}, { ...req });
    if (res.ErrorCode === 1229) {
      const msg: string = i18n.CANNOT_UPDATE_WEBHOOK;
      throw new Error(msg);
    } else if (res.ErrorCode && res.Description) {
      throw new Error(res.Description);
    }
    yield put(updateWebHookSuccessAction(res));
    yield put(getWebHookRequestsAction());
  } catch (e: any) {
    yield put(updateWebHookErrorAction(e.message));
    yield put(getWebHookRequestsAction());
  }
}

function* getAuditLogConfigHandler() {
  try {
    yield put(getAuditLogConfigLoadingAction());
    const res = yield httpGet("/integrations/auditlog");
    if (res && !Array.isArray(res) && res.ErrorCode && (res.Description || res.Message)) {
      throw new Error(res.Description || res.Message);
    }
    yield put(getAuditLogConfigSuccessAction(Array.isArray(res) ? res : res ? [res] : []));
  } catch (e: any) {
    yield put(getAuditLogConfigErrorAction(e.message));
  }
}
function* saveAuditLogConfigHandler(req: AuditLogBodySchema) {
  try {
    yield put(saveAuditLogConfigLoadingAction());
    const res = yield httpPost("/integrations/auditlog", {}, req);
    if (res.ErrorCode && (res.Description || res.Message)) {
      throw new Error(res.Description || res.Message);
    }
    yield put(saveAuditLogConfigSuccessAction(res));
    yield put(getAuditLogConfigAction());
  } catch (e: any) {
    yield put(saveAuditLogConfigErrorAction(e.message));
  }
}
function* updateAuditLogConfigHandler(req: { id: string } & AuditLogBodySchema) {
  try {
    yield put(updateAuditLogConfigLoadingAction());
    const { id, ...payload } = req;
    const res = yield httpPut(`/integrations/auditlog/${id}`, {}, payload);
    if (res.ErrorCode && (res.Description || res.Message)) {
      throw new Error(res.Description || res.Message);
    }
    yield put(updateAuditLogConfigSuccessAction(res));
    yield put(getAuditLogConfigAction());
  } catch (e: any) {
    yield put(updateAuditLogConfigErrorAction(e.message));
  }
}
function* resetAuditLogConfigHandler(id: string | undefined) {
  if (!id) {
    yield put(resetAuditLogConfigErrorAction("Integration ID is required for deletion."));
    return;
  }
  try {
    yield put(resetAuditLogConfigLoadingAction());
    const response = yield httpDelete(`/integrations/auditlog/${id}`, {});
    if (response.ErrorCode && (response.Description || response.Message)) {
      throw new Error(response.Description || response.Message);
    }
    yield put(resetAuditLogConfigSuccessAction(response));
    yield put(getAuditLogConfigAction());
  } catch (e: any) {
    yield put(resetAuditLogConfigErrorAction(e.message));
  }
}
function* getAuditLogLimitsHandler() {
  try {
    yield put(getAuditLogLimitsLoadingAction());
    const res = yield httpGet("/integrations/auditlog/limits");
    if (res.ErrorCode && (res.Description || res.Message)) {
      throw new Error(res.Description || res.Message);
    }
    yield put(getAuditLogLimitsSuccessAction({ Total: res.Total ?? 0, Used: res.Used ?? 0 }));
  } catch (e: any) {
    yield put(getAuditLogLimitsErrorAction(e.message));
  }
}

function* addWebHookHandler(req: AddWebhookRequestSchema) {
  try {
    yield put(addWebHookLoadingAction());

    const res = yield httpPost("/integrations/webhook", {}, req);
    if (res.ErrorCode && res.Description) {
      throw new Error(res.Description);
    }

    yield put(addWebHookSuccessAction(res));
    yield put(getWebHookRequestsAction());
  } catch (e: any) {
    yield put(addWebHookErrorAction(e.message));
    yield put(getWebHookRequestsAction());
  }
}

function* getWebHookEventsHandler() {
  try {
    yield put(getWebhookEventsLoadingAction());
    const response: GetWebhookEventsSchema = yield httpGet("/integrations/webhook/events");
    yield put(getWebhookEventsSuccessAction(response));
  } catch (e: any) {
    yield put(getWebhookEventsErrorAction(e.message));
  }
}

function* getAvailableIntegrations() {
  try {
    yield put(getAvailableIntegrationsLoadingAction());
    const response: AvailableIntegrationsSchema = yield httpGet("/integrations/authorizedintegration");

    if (response.errorCode || !response.AuthorizedIntegrations) {
      throw new Error(response.message || "Unable to get available integrations.");
    }

    yield put(getAvailableIntegrationsSuccessAction(response));
  } catch (e: any) {
    yield put(getAvailableIntegrationsErrorAction(e.message));
  }
}

function* getIntegrationList() {
  try {
    const response = yield httpGet("/integrations/integrationlist");

    if (response.errorCode || !response.Data) {
      throw new Error(response.message || response.description || "Unable to get available integrations.");
    }

    yield put(getIntegrationListSuccess(response));
  } catch (e: any) {
    yield put(getIntegrationListFailure(e.message));
  }
}

function* getIntegrationSubscriptions() {
  try {
    yield put(getIntegrationSubscriptionsLoadingAction());
    const response: IntegrationSubscriptionsSchema = yield httpGet("/integrations/subscriptions");

    if (response.errorCode || !response.Data) {
      throw new Error(response.message || "Unable to get integration subscriptions.");
    }

    yield put(getIntegrationSubscriptionsSuccessAction(response));
  } catch (e: any) {
    yield put(getIntegrationSubscriptionsErrorAction(e.message));
  }
}

function* activateIntegration(payload: ActivateIntegrationArgs) {
  try {
    yield put(activateIntegrationLoadingAction(payload.id));
    const response: IntegrationSubscription = yield httpPut(
      "/integrations/subscriptions/" + payload.id,
      {},
      {
        Id: payload.id,
        Event: payload.Event,
        IsEnable: payload.isEnable
      }
    );

    if ((response as any).ErrorCode || (response as any).errorCode || !response.Id) {
      throw new Error((response as any).Message || (response as any).message || (response as any).description || "Unable to activate integration.");
    }

    const subscriptions: IntegrationSubscription[] = yield select((state: RootSchema) => state.integrations.integrationSubscriptions.Data);
    const copy = cloneDeep(subscriptions);
    for (const data of copy) {
      if (payload.id === data.Id) {
        data.IsEnable = response.IsEnable;
        break;
      }
    }
    yield put(getIntegrationSubscriptionsSuccessAction({ Data: copy } as IntegrationSubscriptionsSchema));
    yield put(activateIntegrationSuccessAction(response as any));
  } catch (e: any) {
    yield put(activateIntegrationErrorAction(e.message));
  }
}

function* deactivateIntegration(payload: DeactivateIntegrationArgs) {
  try {
    yield put(deactivateIntegrationLoadingAction(payload.integrationId));

    const response: any = yield httpDelete("/integrations/subscriptions/" + payload.integrationId, {});

    if (response.ErrorCode || response.errorCode) {
      throw new Error(response.Message || response.message || response.description || "Unable to delete integration.");
    }

    // Remove the deleted subscription from the store
    const subscriptions: IntegrationSubscription[] = yield select((state: RootSchema) => state.integrations.integrationSubscriptions.Data);
    const updated = subscriptions.filter((s) => s.Id !== payload.integrationId);
    yield put(getIntegrationSubscriptionsSuccessAction({ Data: updated } as IntegrationSubscriptionsSchema));
    yield put(deactivateIntegrationSuccessAction(response));
  } catch (e: any) {
    yield put(deactivateIntegrationErrorAction(e.message));
  }
}

function* getIntegrationConfig() {
  try {
    yield put(getIntegrationConfigLoadingAction());

    const response: GetIntegrationConfigSchema = yield httpGet("/integrations/integration/schema");

    if (response.errorCode || !response.schema) {
      throw new Error(response.message || "Permission denied.");
    }

    yield put(getIntegrationConfigSuccessAction(response));
  } catch (e: any) {
    yield put(getIntegrationConfigErrorAction(e.message));
  }
}

function* saveIntegrationData(payload: SaveIntegrationDataArgs) {
  try {
    yield put(saveIntegrationDataLoadingAction());

    if (!payload.Id) {
      // CREATE: single Management API call — credentials, meta, mapping all in one request
      const createPayload = {
        IntegrationName: payload.IntegrationName,
        Event: payload.Event,
        Credentials: payload.Credentials,
        Meta: payload.Meta,
        DownwardMapping: payload.DownwardMapping,
        Mapping: payload.Mapping,
        Filter: payload.Filter,
        PluginFilters: payload.PluginFilters,
        Script: payload.Script
      };
      const createResponse: any = yield httpPost("/integrations/subscriptions", {}, createPayload);

      if (createResponse.ErrorCode || createResponse.errorCode || !createResponse.Id) {
        throw new Error(createResponse.Message || createResponse.message || createResponse.description || "Unable to create integration.");
      }
    } else {
      // EDIT: Management API PUT with full config
      const updatePayload = {
        Id: payload.Id,
        Event: payload.ChangeEventTo || payload.Event,
        Credentials: payload.Credentials,
        Meta: payload.Meta,
        DownwardMapping: payload.DownwardMapping,
        Mapping: payload.Mapping,
        Filter: payload.Filter,
        PluginFilters: payload.PluginFilters,
        Script: payload.Script
      };
      const response: any = yield httpPut("/integrations/subscriptions/" + payload.Id, {}, updatePayload);

      if (response.ErrorCode || response.errorCode || !response.Id) {
        throw new Error(response.Message || response.message || response.description || "Permission denied.");
      }
    }

    // Refetch subscriptions so the installed list reflects the latest state
    yield put(getIntegrationSubscriptionsAction());
    yield put(saveIntegrationDataSuccessAction({ IsPosted: true } as SaveIntegrationDataSchema));
  } catch (e: any) {
    yield put(saveIntegrationDataErrorAction(e.message));
  }
}

/**
 * Watchers
 */
export function* watchGetDataExportDumpRequests() {
  while (true) {
    yield take(IntegrationsConst.GET_DATA_EXPORT_DUMP_REQUESTS);
    yield call(getDataExportDumpRequestsHandler);
  }
}

export function* watchRemoveDataExportDumpRequest() {
  while (true) {
    const {
      payload: { dataExportRequestId }
    } = yield take(IntegrationsConst.REMOVE_DATA_EXPORT_DUMP_REQUEST);
    yield call(removeDataExportDumpRequestHandler, dataExportRequestId);
  }
}

export function* watchAddDataExportDumpRequest() {
  while (true) {
    const {
      payload: { dataExportRequest }
    } = yield take(IntegrationsConst.ADD_DATA_EXPORT_DUMP_REQUEST);
    yield call(addDataExportDumpRequestHandler, dataExportRequest);
  }
}

export function* watchGetExportLogs() {
  while (true) {
    yield take(IntegrationsConst.GET_EXPORT_LOGS);
    yield call(getExportLogsHandler);
  }
}

export function* watchGetExportLogDownloadUrl() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.GET_EXPORT_LOG_DOWNLOAD_URL);
    yield call(getExportLogDownloadUrlHandler, { payload });
  }
}

export function* watchGetWebHookRequests() {
  while (true) {
    yield take(IntegrationsConst.GET_WEB_HOOK_REQUESTS);
    yield call(getWebHookRequestsHandler);
  }
}

export function* watchDeleteWebHook() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.DELETE_WEB_HOOK);
    yield call(deleteWebHookHandler, payload);
  }
}

export function* watchAddWebHook() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.ADD_WEB_HOOK);
    yield call(addWebHookHandler, payload);
  }
}
export function* watchUpdateWebHook() {
  while (true) {
    const { payload, i18n } = yield take(IntegrationsConst.UPDATE_WEB_HOOK);
    yield call(updateWebHookHandler, payload, i18n);
  }
}

export function* watchGetAuditLogConfig() {
  while (true) {
    yield take(IntegrationsConst.GET_AUDIT_LOG_DATA);
    yield call(getAuditLogConfigHandler);
  }
}
export function* watchSaveAuditLogConfig() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.SAVE_AUDIT_LOG_DATA);
    yield call(saveAuditLogConfigHandler, payload);
  }
}
export function* watchUpdateAuditLogConfig() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.UPDATE_AUDIT_LOG_DATA);
    yield call(updateAuditLogConfigHandler, payload);
  }
}
export function* watchGetWebhookEvents() {
  while (true) {
    yield take(IntegrationsConst.GET_WEBHOOK_EVENTS);
    yield call(getWebHookEventsHandler);
  }
}

export function* watchResetAuditLogConfig() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.DELETE_AUDIT_LOG_DATA);
    yield call(resetAuditLogConfigHandler, payload.id);
  }
}

export function* watchGetAuditLogLimits() {
  while (true) {
    yield take(IntegrationsConst.GET_AUDIT_LOG_LIMITS);
    yield call(getAuditLogLimitsHandler);
  }
}

export function* watchGetAvailableIntegrations() {
  while (true) {
    yield take(IntegrationsConst.GET_AVAILABLE_INTEGRATIONS);
    yield call(getAvailableIntegrations);
  }
}

export function* watchGetIntegrationList() {
  yield takeLatest(getIntegrationListRequest.type, getIntegrationList);
}

export function* watchActivateIntegration() {
  // yield takeEvery(
  //   IntegrationsConst.ACTIVATE_INTEGRATION,
  //   ({ payload }: ActionSchema<ActivateIntegrationArgs>) =>
  //     activateIntegration(payload)
  // );
  while (true) {
    const { payload } = yield take(IntegrationsConst.ACTIVATE_INTEGRATION);
    yield call(activateIntegration, payload);
  }
}

export function* watchDeactivateIntegration() {
  // yield takeEvery(
  //   IntegrationsConst.DEACTIVATE_INTEGRATION,
  //   ({ payload }: ActionSchema<DeactivateIntegrationArgs>) =>
  //     deactivateIntegration(payload)
  // );
  while (true) {
    const { payload } = yield take(IntegrationsConst.DEACTIVATE_INTEGRATION);
    yield call(deactivateIntegration, payload);
  }
}

export function* watchGetIntegrationConfig() {
  while (true) {
    yield take(IntegrationsConst.GET_INTEGRATION_CONFIG);
    yield call(getIntegrationConfig);
  }
}

export function* watchSaveIntegrationData() {
  while (true) {
    const { payload } = yield take(IntegrationsConst.SAVE_INTEGRATION_DATA);
    yield call(saveIntegrationData, payload);
  }
}

export function* watchGetIntegrationSubscriptions() {
  while (true) {
    yield take(IntegrationsConst.GET_INTEGRATION_SUBSCRIPTIONS);
    yield call(getIntegrationSubscriptions);
  }
}
